import { useMemo } from 'react';
import { Revalidator, RevalidatorOptions, SWRConfiguration } from 'swr';

import useProductStaticFormatted from './useProductStaticFormatted';
import { FetchError } from 'exceptions/FetchError';
import { useCustomSWR } from 'hooks/useCustomSWR';
import { apiUrls } from 'services/api-urls';
import { fetcher } from 'services/fetcher';

import {
  FormattedProductDataType,
  ProductStaticDataType,
} from 'types/ProductPageV2/Products';

type UseProductStaticResult = {
  data?: FormattedProductDataType;
  error?: Error;
  isLoading: boolean;
  isLoaded: boolean;
};

export default function useProductStatic(
  productUrl: string,
  selectedColor?: string,
  forceSkuUrlAsVariant = false,
  initialData?: ProductStaticDataType,
  keepPreviousData = true
): UseProductStaticResult {
  const productSku = useMemo(() => extractProductSku(productUrl), [productUrl]);
  const {
    data,
    error,
    isLoaded,
    isLoading,
  } = useCustomSWR<ProductStaticDataType>(
    apiUrls.getProductStatic(productUrl),
    fetcher,
    {
      initialData,
      onErrorRetry: onErrorRetryHandler,
      keepPreviousData,
    }
  );

  function extractProductSku(url: string) {
    const startIndex = url.indexOf('P-') + 2;
    const endIndex = url.indexOf('-', startIndex);

    return url.substring(startIndex, endIndex).substring(0, 7);
  }

  const formattedData = useProductStaticFormatted(
    data,
    selectedColor,
    forceSkuUrlAsVariant ? productSku : undefined
  );

  return useMemo(
    () => ({
      data: formattedData,
      error,
      isLoading,
      isLoaded,
    }),
    [formattedData, error, isLoading, isLoaded]
  );
}

function onErrorRetryHandler(
  error: FetchError,
  _key: string,
  config: SWRConfiguration,
  revalidate: Revalidator,
  { retryCount }: RevalidatorOptions
) {
  if (error.status === 404) {
    return;
  }

  setTimeout(() => revalidate({ retryCount }), config.errorRetryInterval);
}
